/**
 * Operator note on a manual target disable (0082): wire shaping and the
 * 「手动摘除 · 备注 · 谁 · 多久前」 line the tenant-routing row and the
 * guaranteed leg status both print.
 */
import {
  normalizeTargetDisabledNote,
  TARGET_DISABLED_NOTE_MAX_CHARS,
} from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../i18n/index.ts';

type Translate = (key: I18nKey, params?: TParams) => string;

export const DISABLED_NOTE_MAX_CHARS = TARGET_DISABLED_NOTE_MAX_CHARS;

const CONTROL_RUN = /[\u0000-\u001F\u007F-\u009F]+/g;

/**
 * Textarea text as the gateway accepts it: line breaks and other control
 * characters become one space, blank means no note, and anything past the
 * cap is cut (the field already stops typing there).
 */
export function disabledNoteForWire(raw: string): string | null {
  const flattened = raw.replace(CONTROL_RUN, ' ');
  const normalized = normalizeTargetDisabledNote(flattened);
  if (normalized.ok) return normalized.value;
  return Array.from(flattened.trim()).slice(0, DISABLED_NOTE_MAX_CHARS).join('');
}

/** 刚刚 / N 分钟前 / N 小时前 / N 天前. Empty when the time is unknown. */
export function agoText(iso: string | null | undefined, nowMs: number, t: Translate): string {
  if (iso === null || iso === undefined) return '';
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return '';
  const minutes = Math.max(0, Math.floor((nowMs - parsed) / 60_000));
  if (minutes < 1) return t('tenant_routing.ago.just_now');
  if (minutes < 60) return t('tenant_routing.ago.minutes', { n: String(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('tenant_routing.ago.hours', { n: String(hours) });
  return t('tenant_routing.ago.days', { n: String(Math.floor(hours / 24)) });
}

export interface DisabledMeta {
  readonly disabled_note?: string | null;
  readonly disabled_at?: string | null;
  readonly disabled_by?: string | null;
}

/** `{note | 无备注} · {who} · {ago}`; parts a gateway did not send are left out. */
export function disabledDetail(meta: DisabledMeta, nowMs: number, t: Translate): string {
  const note = meta.disabled_note ?? '';
  return [
    note.length > 0 ? note : t('tenant_routing.disabled_note.none'),
    meta.disabled_by ?? '',
    agoText(meta.disabled_at, nowMs, t),
  ].filter((part) => part.length > 0).join(' · ');
}
